<template>
    <card title="Consultations">
        <template #header-right>
            <b-button v-if="$permissions.some(p => p.permissionCodeName === 'Admin/Consultation Question Sets')"
                variant="secondary" size="sm" v-b-modal.questionSetsModal>
                <i class="fa fa-gear"></i>
                Manage Question Sets
            </b-button>
        </template>

        <b-row>
            <b-col>
                <SearchComponent v-model="patient" label="Patient" search-u-r-l="/Patient" />
            </b-col>
            <b-col>
                <SearchComponent v-model="store" :disabled="!patient.id" label="Store" search-u-r-l="/Store"
                    prepopulate />
            </b-col>
            <b-col>
                <b-form-group label-for="search-term" class="mb-0">
                    <template #label>
                        Filter:
                        <HelpPopup :icon-muted="true">
                            You can search by:
                            <ul>
                                <li><strong>Patient Name</strong> - partial match</li>
                                <li><strong>Date of Birth</strong> - supports multiple formats:
                                    <ul>
                                        <li>1/8/2009, 01/08/2009</li>
                                        <li>1/8/09, 01/08/09</li>
                                        <li>Partial dates (e.g., "1/8")</li>
                                    </ul>
                                </li>
                                <li><strong>Drug Name</strong> - partial match</li>
                                <li><strong>Type</strong> - General, DiseaseState, Drug, Adherence, Injection</li>
                                <li><strong>Category</strong> - RN, RPH</li>
                                <li><strong>Program Name</strong> - partial match</li>
                                <li><strong>Created By</strong> - partial match</li>
                                <li><strong>Closed By</strong> - partial match</li>
                            </ul>
                        </HelpPopup>
                    </template>
                    <b-form-input id="search-term" v-model="searchTerm" placeholder="Patient Name / DOB / Drug Name / Type / Category" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-button ref="clear" @click="clearInputs">Clear</b-button>
        <b-button ref="openNewConsultationModal" variant="success" :disabled="!store.id || !patient.id"
            @click="openNewConsultationModal">Create New Consultation
        </b-button>

        <hr>

        <b-row>
            <b-col>
                <ConsultationListComponent ref="consultationList" :patientNameAsLink="true" :patient-id="patient.id"
                    :store="store" :enable-cancel="false" :enable-edit="true" :search-term="searchTerm"
                    :show-next-touch-date="true" @consultationCompleted="handleAutoModeCompletion" />
            </b-col>
        </b-row>
        <b-modal id="newConsultationModal" hide-footer no-close-on-backdrop title="New Consultation" size="lg">
            <NewConsultationFormComponent :patient="patient" :store="store" @updateList="updateList" />
        </b-modal>
        <b-modal id="questionSetsModal" hide-footer no-fade title="Question Sets" size="lg">
            <TaskQuestionSets :entity-type="EntityType.Consultation" />
        </b-modal>
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { Store } from '@/models/Store';
import ConsultationListComponent from '@/components/Consultation/ConsultationListComponent.vue';
import NewConsultationFormComponent from '@/components/Consultation/NewConsultationFormComponent.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import TaskQuestionSets from '@/components/Tasks/TaskQuestionSets.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { EntityType } from '@/models/EntityType';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "ConsultationsPage",
    components: {
        ConsultationListComponent,
        SearchComponent,
        NewConsultationFormComponent,
        TaskQuestionSets,
        HelpPopup
    },
})
export default class ConsultationsPage extends Mixins(AutoModeHotkeyMixin) {
    private patient: Patient = new Patient();
    protected store: Store = new Store();
    protected searchTerm: string = "";

    EntityType = EntityType;

    created() {
        this.patient.id = 0;
    }

    async mounted() {
        const consultationId = this.$route.params.consultationId;
        if (consultationId) {
            await this.loadAndShowItem(parseInt(consultationId as string));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldConsultationId = oldRoute.params.consultationId;
        const newConsultationId = newRoute.params.consultationId;

        if (oldConsultationId !== newConsultationId && newConsultationId) {
            console.log('Route consultationId parameter changed, loading new consultation');
            await this.loadAndShowItem(parseInt(newConsultationId));
        }
    }

    patientLabel(option: any) {
        return `${option.lastName}, ${option.firstName}`;
    }

    openNewConsultationModal() {
        this.$bvModal.show("newConsultationModal");
    }

    updateList() {
        this.$bvModal.hide("newConsultationModal");

        const component = this.$refs.consultationList as ConsultationListComponent;
        if (component) {
            component.getConsultation();
        } else {
            console.error("No reference for consultationList component.");
        }

        this.handleAutoModeCompletion();
    }

    clearInputs() {
        this.store = new Store();
        this.patient = Object.assign(new Patient(), { id: 0 });
    }

    // Load and auto-open a specific consultation by ID
    async loadAndShowItem(consultationId: number) {
        try {
            // Look up across every status so deep links to finished consultations still resolve,
            // now that the list defaults to active-only.
            const response = await this.$http.get(`/Consultation/`, { params: { includeAllStatuses: true } });
            const consultations = response.data;
            const consultation = consultations.find((c: any) => c.id === consultationId);

            if (!consultation) {
                this.$notification(NotificationOptions.error('Consultation not found'));
                return;
            }

            // Set the patient and store to match the consultation
            this.patient = Object.assign(new Patient(), { id: consultation.patientId });
            this.store = Object.assign(new Store(), { id: consultation.storeId });

            await this.$nextTick();

            const component = this.$refs.consultationList as ConsultationListComponent;
            if (component) {
                // Trigger the details modal to open for this consultation
                component.detailsConsultation(consultation);
            } else {
                console.error('ConsultationListComponent ref not found');
            }
        } catch (error) {
            console.error('Error loading consultation:', error);
            this.$notification(NotificationOptions.error('Failed to load consultation'));
        }
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }
}
</script>
